import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { GraduationCap, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { useTheme } from './ThemeProvider';

interface SchoolUpdatePromptModalProps {
    isOpen: boolean;
    onClose: () => void;
    tribeName: string;
    schoolName: string;
}

export function SchoolUpdatePromptModal({
    isOpen,
    onClose,
    tribeName,
    schoolName,
}: SchoolUpdatePromptModalProps) {
    const { userProfile, updateProfile } = useUserData();
    const { theme } = useTheme();
    const [education, setEducation] = useState(userProfile?.education || schoolName || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        const trimmed = education.trim();
        setSaving(true);
        try {
            await updateProfile({ education: trimmed });
            toast.success('Profile updated!');
        } catch (e) {
            toast.error('Could not update profile');
        } finally {
            setSaving(false);
            onClose();
        }
    };

    const handleSkip = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div
                className="fixed inset-0 z-[10000010] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                onClick={handleSkip}
            >
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 32 }}
                    transition={{ duration: 0.2 }}
                    className="w-full max-w-sm rounded-2xl shadow-2xl border border-black/10 dark:border-white/10 overflow-hidden"
                    style={{ backgroundColor: theme === 'dark' ? '#1a1a1a' : '#FDFBF7' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Top dismiss button */}
                    <div className="flex justify-end px-4 pt-4">
                        <button
                            onClick={handleSkip}
                            className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-black/40 dark:text-white/40"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="px-6 pb-6">
                        {/* Icon + heading */}
                        <div className="flex flex-col items-center text-center mb-5">
                            <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-3">
                                <GraduationCap className="w-7 h-7 text-blue-500 dark:text-blue-400" />
                            </div>
                            <h2 className="text-base font-bold text-black dark:text-white">
                                You're in! 🎉
                            </h2>
                            <p className="text-sm text-black/50 dark:text-white/50 mt-1 leading-relaxed">
                                You've been approved to join <span className="font-semibold text-black/70 dark:text-white/70">{tribeName}</span>.
                                {schoolName ? ` Want to add ${schoolName} to your profile?` : ' Want to add your school to your profile?'}
                            </p>
                        </div>

                        {/* Education input */}
                        <label className="block text-sm font-bold text-black dark:text-white mb-2">
                            School / University
                        </label>
                        <Input
                            value={education}
                            onChange={(e) => setEducation(e.target.value)}
                            placeholder={schoolName || 'University name'}
                            className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 h-11 text-base rounded-xl"
                            autoFocus
                        />

                        <div className="flex gap-2 mt-5">
                            <Button
                                variant="ghost"
                                onClick={handleSkip}
                                className="flex-1 text-black/50 dark:text-white/50"
                            >
                                Skip
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white border-none font-bold disabled:opacity-50"
                            >
                                {saving ? 'Saving…' : 'Update Profile'}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
